<script setup lang="ts">
import {useHolidaysStore} from "@/stores/holidays.store.ts";
import {computed, ref} from "vue";
import HolidaysEditor from "@/components/admin/HolidaysEditor.vue";
import {humanDateEEEMMMdd} from "@/helpers/date.helper";
import {useAuthStore} from "@/stores";
import type {Holiday} from "@/types/DataTypes.ts";
import {isDark} from "@/composables/useDark";


const holidaysStore = useHolidaysStore();
const selectedYear = ref(new Date().getFullYear());
const currentYear = new Date().getFullYear();
const loading = ref(false);
const editingHolidayId = ref<number | null>(null);
const editingDate = ref<Date | null>(null);
const datePopover = ref();

const holidaysMap = computed(() => {
  const map = holidaysStore.holidays?.filter(holiday => holiday.year === selectedYear.value).reduce((acc, holiday) => {
    if (!acc[holiday.location]) {
      acc[holiday.location] = [];
    }
    acc[holiday.location].push(holiday);
    return acc;
  }, {} as Record<string, any[]>);
  
  // Sort holidays by date within each location
  Object.keys(map).forEach(location => {
    map[location].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  });
  
  return map;
})

const locationOrder = ['UA', 'PL', 'US', 'TH', 'DE'];

const holidaysKeys = computed(() => {
  const keys = Object.keys(holidaysMap.value);
  return keys.sort((a, b) => {
    const indexA = locationOrder.indexOf(a);
    const indexB = locationOrder.indexOf(b);
    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only A is in the order array, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the order array, it comes first
    if (indexB !== -1) return 1;
    // If neither is in the order array, sort alphabetically
    return a.localeCompare(b);
  });
})

const authStore = useAuthStore();

const years = computed(() => {
  const years = new Set();
  const currentYear = new Date().getFullYear();
  years.add(currentYear);
  years.add(currentYear + 1);
  holidaysStore.holidays?.forEach(holiday => {
    years.add(holiday.year);
  });
  return Array.from(years).sort((a: any, b: any) => b - a);
});

const editor = ref();

const openEditor = (e: any) => {
  editor.value.show(e);
}

const closeEditor = () => {
  editor.value.hide();
}

const isFutureYear = computed(() => selectedYear.value > currentYear);

const copyFromPreviousYear = async () => {
  loading.value = true;
  try {
    await holidaysStore.copyHolidaysFromPreviousYear(selectedYear.value);
  } finally {
    loading.value = false;
  }
}

const openDateEditor = (e: any, holiday: Holiday) => {
  editingHolidayId.value = holiday.id;
  editingDate.value = new Date(holiday.date);
  datePopover.value.show(e);
}

const confirmDateChange = async () => {
  if (editingHolidayId.value && editingDate.value) {
    loading.value = true;
    try {
      const dateStr = editingDate.value.toISOString().split('T')[0];
      await holidaysStore.updateHolidayDate(editingHolidayId.value, dateStr);
    } finally {
      loading.value = false;
      datePopover.value.hide();
      editingHolidayId.value = null;
      editingDate.value = null;
    }
  }
}

const cancelDateChange = () => {
  datePopover.value.hide();
  editingHolidayId.value = null;
  editingDate.value = null;
}
</script>

<template>
  <div v-if="holidaysStore.holidays">
    <div class="flex flex-row gap-2">
      <Select :options="years" v-model="selectedYear" label="Year"/>
      <Button 
        v-if="authStore.isAdmin && isFutureYear" 
        label="Copy from previous year" 
        icon="pi pi-copy" 
        @click="copyFromPreviousYear"
        :loading="loading"
        severity="secondary"
      />
      <Button label="Add holidays" icon="pi pi-plus" @click="openEditor" v-if="authStore.isAdmin"/>
    </div>

    <div class="flex flex-wrap gap-2" :key="selectedYear">
      <Fieldset v-for="location in holidaysKeys" :key="location" :legend="location">
        <div class="flex flex-row gap-4 mb-4" v-for="holiday in holidaysMap[location]" :key="holiday.id">
          <span class="w-[8rem]">
            <Tag 
              :value="humanDateEEEMMMdd(holiday.date)" 
              class="w-full cursor-pointer hover:bg-primary-600 hover:text-white" 
              @click="(e: any) => authStore.isAdmin && openDateEditor(e, holiday)"
            />
          </span>
          <span>{{ holiday.name }}</span>
        </div>
      </Fieldset>
    </div>

    <Popover ref="editor">
      <HolidaysEditor :year="selectedYear" @done="closeEditor"/>
    </Popover>

    <Popover ref="datePopover">
      <div class="flex flex-col gap-3 p-2">
        <label class="font-semibold">Change Date</label>
        <DatePicker v-model="editingDate" showIcon iconDisplay="input" :is-dark="isDark()" />
        <div class="flex gap-2 justify-end">
          <Button label="Cancel" severity="secondary" size="small" @click="cancelDateChange" />
          <Button label="Confirm" size="small" @click="confirmDateChange" :loading="loading" />
        </div>
      </div>
    </Popover>
  </div>
</template>
